import { useEffect } from 'react';
import { Route, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { FlatRoutes } from '@backstage/core-app-api';
import { convertLegacyApp } from '@backstage/core-compat-api';
import { UserIdentity } from '@backstage/core-components';
import { createApp } from '@backstage/frontend-defaults';
import {
  AnalyticsImplementationBlueprint,
  RouterBlueprint,
  SignInPageBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';

import { AppThemes } from '@spotify/confidence-app-theme';
import {
  ConfidenceAnalytics,
  Redirect,
  confidenceUserApiRef,
  splitComponent,
} from '@spotify/confidence-core-react';
import { EditorDataExtensions } from '@spotify/confidence-editor-react';
import { bindOnboardingRoutes } from '@spotify/confidence-onboarding-react';
import { FeedItemExtensions } from '@spotify/plugin-confidence-activity-react';
import {
  ConfidenceAdminPage,
  bindAdminRoutes,
  confidenceAdminPlugin,
} from '@spotify/plugin-confidence-admin';
import {
  CollectionEntryExtensions,
  FavoritesPage,
} from '@spotify/plugin-confidence-collections-react';
import {
  ConfidenceEventsPage,
  bindEventsRoutes,
} from '@spotify/plugin-confidence-events';
import { ConfidenceExplorePage } from '@spotify/plugin-confidence-explore';
import {
  ConfidenceFlagsPage,
  FlagCollectionEntryExtension,
  FlagRuleChangeExtension,
  FlagSchemaChangeExtension,
  FlagStringChangeExtension,
  FlagVariantChangeExtension,
  bindFlagRoutes,
  flagsPlugin,
} from '@spotify/plugin-confidence-flags';
import {
  ConfidenceMaterializedSegmentsExtension,
  bindMaterializedSegmentsRoutes,
  confidenceMaterializedSegmentsPlugin,
} from '@spotify/plugin-confidence-flags-module-materialized-segments';
import {
  ConfidenceSegmentsExtension,
  bindSegmentsRoutes,
  confidenceSegmentsPlugin,
} from '@spotify/plugin-confidence-flags-module-segments';
import { FlagUpdatesChangeExtensions } from '@spotify/plugin-confidence-flags-react';
import {
  ConfidenceInitiativesPage,
  bindInitiativesRoutes,
} from '@spotify/plugin-confidence-initiatives';
import {
  ConfidenceInsightsPage,
  bindInsightsRoutes,
} from '@spotify/plugin-confidence-insights';
import {
  ConfidenceMeasurementsPage,
  ConfidenceMetricsPage,
  MetricCollectionEntryExtension,
  bindMetricsRoutes,
  metricsPlugin,
} from '@spotify/plugin-confidence-metrics';
import { DebugPage } from '@spotify/plugin-confidence-operations';
import {
  ConfidenceSurfacesPage,
  SurfaceCollectionEntryExtension,
  bindSurfacesRoutes,
  surfacesPlugin,
} from '@spotify/plugin-confidence-surfaces';
import {
  AudienceFlagsChangeExtension,
  AudienceSegmentChangeExtension,
  ConfidenceWorkflowsPage,
  InitiativeChangeExtension,
  LabelsChangeExtension,
  MetricDeliveryFeedItemExtension,
  PlanningChangeExtension,
  RecommendationChangedFeedItemExtension,
  SampleRatioMismatchFeedItemExtension,
  SampleSizeChangeExtension,
  StatsChangeExtension,
  SurfacesChangeExtension,
  TrafficValidationFeedItemExtension,
  WorkflowInstanceCollectionEntryExtension,
  WorkflowInstanceFeedItemExtension,
  WorkflowOwnerChangeExtension,
  WorkflowStateChangeExtension,
  WorkflowStringChangeExtension,
  bindWorkflowsRoutes,
  workflowsPlugin,
} from '@spotify/plugin-confidence-workflows';
import {
  ABTestWorkflowExtension,
  MetricsChangeExtension,
  ReportChangeExtension,
  SegmentChangeExtension,
  TreatmentsChangeExtension,
  WorkflowEditorDataExtension,
} from '@spotify/plugin-confidence-workflows-module-ab-test';
import { WorkflowInstanceRuleChangeExtension } from '@spotify/plugin-confidence-workflows-module-flags';
import { ReanalysisWorkflowExtension } from '@spotify/plugin-confidence-workflows-module-reanalysis';
import { RolloutWorkflowExtension } from '@spotify/plugin-confidence-workflows-module-rollout';
import {
  WorkflowExtensions,
  WorkflowExtensionsInitializer,
  WorkflowUpdatesChangeExtensions,
} from '@spotify/plugin-confidence-workflows-react';

import { AppApis } from './apis';
import { confidenceApi } from './apis/Apis';
import CommentsHandler from './components/CommentsHandler';
import { ConnectPage } from './components/ConnectPage/ConnectPage';
import { EmailVerifiedPage } from './components/EmailVerified';
import { HomePage } from './components/HomePage';
import { InvitationPage } from './components/InvitationPage';
import { LoginPage } from './components/LoginPage';
import RedirectHandler from './components/RedirectHandler';
import ReviewsHandler from './components/ReviewsHandler';
import { RootWrapper } from './components/RootWrapper';
import { Layout, Sidebar } from './components/Sidebar';
import {
  InviteRequiredPage,
  SignUpPage,
  UnsupportedMarketRoute,
} from './components/SignUp';
import { SlackPage } from './components/SlackPage';

const LegacySegmentRedirect = () => {
  const { id } = useParams();
  return <Redirect to={`/segments/${id}`} />;
};

const workflowExtensions = (
  <WorkflowExtensions>
    <ABTestWorkflowExtension />
    <RolloutWorkflowExtension />
    <ReanalysisWorkflowExtension />
  </WorkflowExtensions>
);

const flagChangeExtensions = (
  <FlagUpdatesChangeExtensions>
    <WorkflowInstanceRuleChangeExtension />
    <FlagSchemaChangeExtension />
    <FlagVariantChangeExtension />
    <FlagRuleChangeExtension />
    <FlagStringChangeExtension />
  </FlagUpdatesChangeExtensions>
);

const workflowChangeExtensions = (
  <WorkflowUpdatesChangeExtensions>
    <TreatmentsChangeExtension />
    <AudienceFlagsChangeExtension />
    <AudienceSegmentChangeExtension />
    <InitiativeChangeExtension />
    <LabelsChangeExtension />
    <PlanningChangeExtension />
    <StatsChangeExtension />
    <MetricsChangeExtension />
    <ReportChangeExtension />
    <SegmentChangeExtension />
    <WorkflowStateChangeExtension />
    <WorkflowOwnerChangeExtension />
    <SurfacesChangeExtension />
    <WorkflowStringChangeExtension />
    <SampleSizeChangeExtension />
  </WorkflowUpdatesChangeExtensions>
);

const editorDataExtensions = (
  <EditorDataExtensions>
    <WorkflowEditorDataExtension />
  </EditorDataExtensions>
);

const activityFeedExtensions = (
  <FeedItemExtensions>
    <WorkflowInstanceFeedItemExtension />
    <RecommendationChangedFeedItemExtension />
    <TrafficValidationFeedItemExtension />
    <MetricDeliveryFeedItemExtension />
    <SampleRatioMismatchFeedItemExtension />
  </FeedItemExtensions>
);

const collectionEntryExtensions = (
  <CollectionEntryExtensions>
    <MetricCollectionEntryExtension />
    <FlagCollectionEntryExtension />
    <SurfaceCollectionEntryExtension />
    <WorkflowInstanceCollectionEntryExtension />
  </CollectionEntryExtensions>
);

const SignUpRoute = splitComponent('self-serve', { enabled: false }, value =>
  value.enabled ? SignUpPage : InviteRequiredPage,
);

const routes = (
  <FlatRoutes>
    {/* ========== PUBLIC ROUTES ========== */}
    {/* These routes render without authentication (see RootWrapper.tsx PUBLIC_ROUTES) */}
    <Route path="/login" element={<LoginPage />} />
    <Route path="/invitation" element={<InvitationPage />} />
    <Route
      path="/signup/unsupported-market"
      element={<UnsupportedMarketRoute />}
    />
    <Route path="/signup" element={<SignUpRoute />} />
    <Route path="/signup/:id" element={<SignUpPage />} />
    <Route path="/beta/:id" element={<SignUpPage />} />
    <Route path="/email-verified" element={<EmailVerifiedPage />} />

    {/* ========== PROTECTED ROUTES ========== */}
    {/* All routes below require authentication (enforced by RootWrapper) */}
    <Route
      path="/"
      element={
        <HomePage>
          {activityFeedExtensions}
          <WorkflowExtensionsInitializer>
            {workflowExtensions}
          </WorkflowExtensionsInitializer>
        </HomePage>
      }
    />
    <Route path="/slack" element={<SlackPage />} />
    <Route path="/connect" element={<ConnectPage />} />
    <Route path="/redirect" element={<RedirectHandler />} />
    <Route path="/comments" element={<CommentsHandler />} />
    <Route path="/reviews" element={<ReviewsHandler />} />
    <Route
      path="/workflows"
      element={
        <ConfidenceWorkflowsPage>
          {workflowExtensions}
          {workflowChangeExtensions}
          {editorDataExtensions}
        </ConfidenceWorkflowsPage>
      }
    />
    <Route
      path="/flags"
      element={
        <ConfidenceFlagsPage>
          <WorkflowExtensionsInitializer>
            {workflowExtensions}
          </WorkflowExtensionsInitializer>
          {flagChangeExtensions}
        </ConfidenceFlagsPage>
      }
    />
    <Route path="/flags/segments/:id" element={<LegacySegmentRedirect />} />
    <Route path="/segments" element={<ConfidenceSegmentsExtension />} />
    <Route
      path="/materializedSegments"
      element={<ConfidenceMaterializedSegmentsExtension />}
    />
    <Route path="/metrics" element={<ConfidenceMetricsPage />} />
    <Route path="/measurements" element={<ConfidenceMeasurementsPage />} />
    <Route
      path="/favorites"
      element={
        <FavoritesPage>
          <WorkflowExtensionsInitializer>
            {workflowExtensions}
          </WorkflowExtensionsInitializer>
          {collectionEntryExtensions}
        </FavoritesPage>
      }
    />
    <Route path="/events" element={<ConfidenceEventsPage />} />
    <Route
      path="/surfaces"
      element={
        <ConfidenceSurfacesPage>{workflowExtensions}</ConfidenceSurfacesPage>
      }
    />
    <Route
      path="/initiatives"
      element={
        <ConfidenceInitiativesPage>
          {workflowExtensions}
        </ConfidenceInitiativesPage>
      }
    />
    <Route
      path="/admin"
      element={
        <ConfidenceAdminPage>{collectionEntryExtensions}</ConfidenceAdminPage>
      }
    />
    <Route path="/debug" element={<DebugPage />} />
    <Route path="/insights" element={<ConfidenceInsightsPage />} />
    <Route path="/explore" element={<ConfidenceExplorePage />} />
  </FlatRoutes>
);

const Router = RouterBlueprint.make({
  params: {
    component: ({ children }) => {
      const router = createBrowserRouter([
        { path: '*', Component: () => <>{children}</> },
      ]);

      return <RouterProvider router={router} />;
    },
  },
});

// Confidence auth setup should be re-done with Backstage identity
// to make this all work properly (just workaround for now)
const SignInPage = SignInPageBlueprint.make({
  params: {
    loader: () =>
      Promise.resolve(({ onSignInSuccess, children }) => {
        useEffect(() => {
          onSignInSuccess(UserIdentity.createGuest());
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        return children;
      }),
  },
});

const confidenceAnalyticsImplementation = AnalyticsImplementationBlueprint.make(
  {
    params: defineParams =>
      defineParams({
        deps: { confidenceUserApi: confidenceUserApiRef },
        factory: ({ confidenceUserApi }) =>
          new ConfidenceAnalytics({ confidenceApi, confidenceUserApi }),
      }),
  },
);

export const AppModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SignInPage,
    Router,
    RootWrapper,
    Layout,
    Sidebar,
    ...AppThemes,
    ...AppApis,
    confidenceAnalyticsImplementation,
  ],
});

const app = createApp({
  features: [...convertLegacyApp(routes), AppModule],
  bindRoutes({ bind }) {
    bindWorkflowsRoutes(bind, {
      flag: flagsPlugin.routes.flag,
      flags: flagsPlugin.routes.flags,
      metric: metricsPlugin.routes.metric,
      metrics: metricsPlugin.routes.metrics,
      entity: confidenceAdminPlugin.routes.entity,
      entities: confidenceAdminPlugin.routes.entities,
      assignmentTable: confidenceAdminPlugin.routes.assignmentTable,
      assignmentTablesCreate:
        confidenceAdminPlugin.routes.assignmentTablesCreate,
      factTable: confidenceAdminPlugin.routes.factTable,
      surface: surfacesPlugin.routes.surface,
      labels: confidenceAdminPlugin.routes.labels,
    });

    bindMetricsRoutes(bind, {
      workflowInstance: workflowsPlugin.routes.workflowInstance,
      entities: confidenceAdminPlugin.routes.entities,
      entity: confidenceAdminPlugin.routes.entity,
      assignmentTable: confidenceAdminPlugin.routes.assignmentTable,
      assignmentTablesCreate:
        confidenceAdminPlugin.routes.assignmentTablesCreate,
      factTable: confidenceAdminPlugin.routes.factTable,
      factTables: confidenceAdminPlugin.routes.factTables,
      factTablesCreate: confidenceAdminPlugin.routes.factTablesCreate,
      metricsDataWarehouse: confidenceAdminPlugin.routes.metricsDataWarehouse,
      labels: confidenceAdminPlugin.routes.labels,
    });

    bindFlagRoutes(bind, {
      workflow: workflowsPlugin.routes.workflow,
      workflowInstance: workflowsPlugin.routes.workflowInstance,
      clients: confidenceAdminPlugin.routes.clients,
      clientSchema: confidenceAdminPlugin.routes.clientSchema,
      client: confidenceAdminPlugin.routes.client,
      labels: confidenceAdminPlugin.routes.labels,

      // Segments and materialized segments used by flags plugin
      segments: confidenceSegmentsPlugin.routes.segments,
      segment: confidenceSegmentsPlugin.routes.segment,
      materializedSegments:
        confidenceMaterializedSegmentsPlugin.routes.materializedSegments,
      materializedSegment:
        confidenceMaterializedSegmentsPlugin.routes.materializedSegment,
      materializedSegmentLoadJob:
        confidenceMaterializedSegmentsPlugin.routes.materializedSegmentLoadJob,
    });

    // Bind segments module routes for standalone navigation
    bindSegmentsRoutes(bind, {
      segments: confidenceSegmentsPlugin.routes.segments,
      segment: confidenceSegmentsPlugin.routes.segment,
    });

    // Bind materialized segments module routes for standalone navigation
    bindMaterializedSegmentsRoutes(bind, {
      materializedSegments:
        confidenceMaterializedSegmentsPlugin.routes.materializedSegments,
      materializedSegment:
        confidenceMaterializedSegmentsPlugin.routes.materializedSegment,
      materializedSegmentLoadJob:
        confidenceMaterializedSegmentsPlugin.routes.materializedSegmentLoadJob,
    });

    bindEventsRoutes(bind, {
      entity: confidenceAdminPlugin.routes.entity,
      entities: confidenceAdminPlugin.routes.entities,
    });

    bindInsightsRoutes(bind, {
      surface: surfacesPlugin.routes.surface,
    });

    bindSurfacesRoutes(bind, {});

    bindInitiativesRoutes(bind, {});

    bindAdminRoutes(bind, {
      assignmentTables: confidenceAdminPlugin.routes.assignmentTables,
      assignmentTable: confidenceAdminPlugin.routes.assignmentTable,
      factTables: confidenceAdminPlugin.routes.factTables,
      dimensionTables: confidenceAdminPlugin.routes.dimensionTables,
      entity: confidenceAdminPlugin.routes.entity,
      entities: confidenceAdminPlugin.routes.entities,
    });

    bindOnboardingRoutes(bind, {
      adminClients: confidenceAdminPlugin.routes.clients,
      flags: flagsPlugin.routes.flags,
      metrics: metricsPlugin.routes.metrics,
      workflow: workflowsPlugin.routes.workflow,
      metricsDataWarehouse: confidenceAdminPlugin.routes.metricsDataWarehouse,
      flagAppliedConnections:
        confidenceAdminPlugin.routes.flagAppliedConnections,
      assignmentTables: confidenceAdminPlugin.routes.assignmentTables,
      factTables: confidenceAdminPlugin.routes.factTables,
    });
  },
});

export default app.createRoot();
